import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { Capacitor } from '@capacitor/core';
import { colors, safeAreaInset } from '../../styles';

type SafeAreaProps = PropsWithChildren<{
  top?: boolean;
  bottom?: boolean;
  left?: boolean;
  right?: boolean;
  backgroundColor?: string;
}>;

/**
 * SafeArea component - Handles safe area insets for content that should not be hidden
 * behind system UI elements (status bar, notch, Dynamic Island, home indicator).
 *
 * IMPORTANT: This component is critical because StatusBar.overlaysWebView is set to `true`
 * in capacitor.config.json (both iOS and Android). Without proper safe area handling,
 * content can be hidden behind:
 * - iPhone notch (iPhone X-13 Pro)
 * - iPhone Dynamic Island (iPhone 14 Pro+)
 * - Android status bar
 * - Bottom home indicator/navigation bar
 *
 * Usage: Wrap pages or modals that need full-height layouts with safe area padding.
 * For standard pages, use the Page component which handles safe areas automatically.
 */
const SafeArea: React.FC<SafeAreaProps> = ({
  children,
  top = true,
  bottom = true,
  left = true,
  right = true,
  backgroundColor,
}) => {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  return (
    <div
      css={css`
        /* Base container styles */
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: ${backgroundColor || 'var(--background-color, #fffefc)'};
        position: relative;
        overflow: hidden;

        /* Safe area insets with platform-specific adjustments */
        padding-top: ${!top
          ? '0'
          : !isNative
            ? '0px'
            : platform === 'android'
              ? `calc(${safeAreaInset.top} + 8px)`
              : safeAreaInset.top};
        padding-bottom: ${!bottom ? '0' : !isNative ? '0px' : safeAreaInset.bottom};
        padding-left: ${!left ? '0' : !isNative ? '0px' : safeAreaInset.left};
        padding-right: ${!right ? '0' : !isNative ? '0px' : safeAreaInset.right};

        /* Dark mode support */
        html[data-theme='dark'] & {
          background-color: ${backgroundColor || colors.liquorice};
        }
      `}
    >
      {children}
    </div>
  );
};

export default SafeArea;
